import { motion, useReducedMotion } from 'framer-motion';
import { useSite } from '../content';

interface ContactProps {
  onOpenContact: (inquiryType?: string) => void;
}

export function Contact({ onOpenContact }: ContactProps) {
  const prefersReducedMotion = useReducedMotion();
  const site = useSite();
  const { contact } = site;
  const contactEmail = contact.email;
  const contactPhone = contact.phone;

  return (
    <section
      id="contact"
      aria-labelledby="contact-heading"
      className="relative w-full py-32 md:py-48 bg-slate-texture bg-cover bg-center overflow-hidden"
    >
      <div aria-hidden="true" className="absolute inset-0 bg-slate-dark/90" />

      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center flex flex-col items-center">
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: prefersReducedMotion ? 0 : 1 }}
        >
          <h2 id="contact-heading" className="font-serif text-5xl md:text-7xl text-white mb-6">
            {contact.heading}
          </h2>
          <p className="font-sans text-textlight/80 text-lg md:text-xl mb-12 max-w-lg mx-auto">
            {contact.body}
          </p>

          {(contactEmail || contactPhone) && (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-16">
              {contactEmail && (
                <a
                  href={`mailto:${contactEmail}`}
                  className="font-sans text-textlight hover:text-bronze transition-colors"
                >
                  {contactEmail}
                </a>
              )}
              {contactEmail && contactPhone && (
                <span aria-hidden="true" className="hidden sm:block w-1 h-1 rounded-full bg-textlight/30" />
              )}
              {contactPhone && (
                <a
                  href={`tel:${contactPhone.replace(/[^+\d]/g, '')}`}
                  className="font-sans text-textlight hover:text-bronze transition-colors"
                >
                  {contactPhone}
                </a>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={() => onOpenContact()}
            className="px-10 py-4 bg-burgundy hover:bg-burgundy/80 text-textlight font-sans tracking-widest uppercase text-sm rounded-full transition-all duration-300 shadow-lg min-h-[44px]"
          >
            {contact.ctaLabel}
          </button>
        </motion.div>
      </div>
    </section>
  );
}

export function SiteFooter() {
  const site = useSite();
  const copy = site.site.footerCopyright.replace('{year}', String(new Date().getFullYear()));
  return (
    <footer className="w-full p-6 border-t border-textlight/10 text-center bg-slate-dark">
      <p className="font-sans text-xs text-textlight/70 uppercase tracking-widest">{copy}</p>
    </footer>
  );
}
